import { ReferenceField, FunctionField, useRecordContext } from 'react-admin';
import { Box, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { EventType, Event } from '../../types';
import { MaterialChangedTagDisplay } from './MaterialChangedTagDisplay';

interface TagInfo {
  number?: string;
  info?: {
    material?: string;
    color?: string;
    profile?: string;
    weight?: number;
  };
}

const MaterialChangedContent = ({
  eventType,
  record,
}: {
  record?: Event;
  eventType: EventType;
}) => {
  if (!record?.rawContent) return null;
  if (eventType.name !== 'material-changed') return null;

  try {
    const rawData = JSON.parse(record.rawContent);
    const fields = rawData?.fields;

    if (!fields?.oldTag && !fields?.newTag) return null;

    const oldTag: TagInfo = fields.oldTag;
    const newTag: TagInfo = fields.newTag;

    // Extract dispenser number from device (RFID-READER-<numero>)
    let dispenserNumber: number | null = null;
    if (fields.device && typeof fields.device === 'string') {
      const match = fields.device.match(/RFID-READER-(\d+)/);
      if (match) {
        dispenserNumber = parseInt(match[1], 10) + 1;
      }
    }

    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="subtitle2">Material Changed</Typography>
          {dispenserNumber !== null && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • Dispenser {dispenserNumber}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            gridColumn: { xs: '1', sm: '1 / -1' },
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <MaterialChangedTagDisplay tag={oldTag} label="Previous Tag" />
          </Box>
          <ArrowForwardIcon sx={{ color: 'primary.main', fontSize: 32, flexShrink: 0, mt: 3 }} />
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <MaterialChangedTagDisplay tag={newTag} label="New Tag" />
          </Box>
        </Box>
      </Box>
    );
  } catch {
    // Error parsing rawContent, silently ignore
    return null;
  }
};

export const MaterialChanged = () => {
  const record = useRecordContext<Event>();
  return (
    <ReferenceField source="eventTypeId" reference="event-types" link={false}>
      <FunctionField
        render={(eventType: EventType) => (
          <MaterialChangedContent record={record} eventType={eventType} />
        )}
      />
    </ReferenceField>
  );
};
